import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";

import { auth } from "../firebase/config";
import { createUserProfile } from "../services/user-service";

export const PASSWORD_MIN = 6;

// Firebase error codes are for machines. These are for people.
function friendlyAuthError(error) {
  switch (error?.code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "That email and password don't match.";
    case "auth/email-already-in-use":
      return "That email already has an account — log in instead.";
    case "auth/invalid-email":
      return "That doesn't look like a valid email.";
    case "auth/weak-password":
      return `Use at least ${PASSWORD_MIN} characters.`;
    case "auth/too-many-requests":
      return "Too many attempts. Give it a minute and try again.";
    case "auth/network-request-failed":
      return "Can't reach the network. Check your connection.";
    default:
      return "Something went wrong. Try again.";
  }
}

export function useAuth() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authBusy, setAuthBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setAuthError("");

      if (!currentUser) {
        setAuthLoading(false);
        return;
      }

      try {
        // Idempotent: creates users/{uid} only if it isn't there yet, so this is
        // safe for both brand-new signups and long-standing accounts.
        await createUserProfile(currentUser);
      } catch (error) {
        console.error("Error creating user profile:", error);
      }

      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = useCallback(async () => {
    if (authBusy) return;

    const cleanEmail = email.trim();

    if (!cleanEmail || !password) {
      setAuthError("Enter your email and password.");
      return;
    }

    try {
      setAuthBusy(true);
      setAuthError("");
      await signInWithEmailAndPassword(auth, cleanEmail, password);
    } catch (error) {
      console.error("Login error:", error);
      setAuthError(friendlyAuthError(error));
    } finally {
      setAuthBusy(false);
    }
  }, [authBusy, email, password]);

  const signup = useCallback(async () => {
    if (authBusy) return;

    const cleanEmail = email.trim();

    if (!cleanEmail) {
      setAuthError("Enter your email.");
      return;
    }

    if (password.length < PASSWORD_MIN) {
      setAuthError(`Passwords need at least ${PASSWORD_MIN} characters.`);
      return;
    }

    if (password !== confirmPassword) {
      setAuthError("Those passwords don't match.");
      return;
    }

    try {
      setAuthBusy(true);
      setAuthError("");

      // onAuthStateChanged fires right after this and creates users/{uid}.
      // The profile gate then sends the user straight into setup.
      await createUserWithEmailAndPassword(auth, cleanEmail, password);
    } catch (error) {
      console.error("Signup error:", error);
      setAuthError(friendlyAuthError(error));
    } finally {
      setAuthBusy(false);
    }
  }, [authBusy, email, password, confirmPassword]);

  const logout = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  }, []);

  const resetAuthForm = useCallback(() => {
    setPassword("");
    setConfirmPassword("");
    setAuthError("");
  }, []);

  return useMemo(
    () => ({
      user,
      authLoading,
      authBusy,
      email,
      password,
      confirmPassword,
      authError,
      login,
      signup,
      logout,
      resetAuthForm,
      setEmail,
      setPassword,
      setConfirmPassword,
    }),
    [
      user,
      authLoading,
      authBusy,
      email,
      password,
      confirmPassword,
      authError,
      login,
      signup,
      logout,
      resetAuthForm,
    ],
  );
}

export default useAuth;
